import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HeaderWrapper } from '@components/layout/header/header-wrapper';
import { routesMap } from '@shared/lib';
import { Button } from '@/components/ui/button';
import { AccountSelect } from '@components/form/select/confirmable-select/ready';
import { IAccount, useGetAccounts } from '@entities/accounts/model';
import {
  formatBalance,
  formatBalanceWithCurrency,
} from '@components/transactions/util';
import { PlanCard } from '@entities/plan/ui/plan-card';
import { useSubscribeToPlan, useTariffs } from '@entities/plan/model/usePlans';
import { Loader } from '@ui/loader';
import { useToast } from '@shared/hooks';
import { useCurrencyRate } from './useActivationDetails';
import { Details } from '../../entities/plan/ui/details';
import PageLoader from '@ui/PageLoader';
import { usePlanFreeUpdateStore } from '@entities/plan/model/planFeeUpdateStore';

export const PlanActivationPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();

  const { planType } = useParams<{ planType: string }>();
  const [account, setAccount] = useState<IAccount | null>(null);
  const { checkingAccounts, isLoading: isAccountsLoading } = useGetAccounts();
  const { tariffs, isLoading: isPlansLoading } = useTariffs();
  const { mutateAsync: subscribeToPlan, isPending } = useSubscribeToPlan();
  const { setShouldShowFeeUpdateModal } = usePlanFreeUpdateStore();

  const plan = tariffs.find((plan) => plan.id === planType)! || {};

  const fromAccount = checkingAccounts?.find(
    (a) => a.currency === plan.currency,
  );

  const { rate } = useCurrencyRate({
    fromAccount: fromAccount ?? null,
    toAccount: account ?? null,
  });

  const convertedToPlanCurrencyAccountBalance = (account?.balance ?? 0) * rate!;
  const formattedAccountBalance = formatBalanceWithCurrency({
    balance: convertedToPlanCurrencyAccountBalance,
    currency: plan.currency,
  });

  const annualPrice = plan.cost;
  const annualFormattedPrice = formatBalanceWithCurrency({
    balance: plan.cost,
    currency: plan.currency,
  });
  const annualFormattedPriceWithoutSpacing = formatBalanceWithCurrency({
    balance: plan.cost,
    currency: plan.currency,
    withoutSpaceBetweenBalanceAndCurrency: true,
  });

  const remainingBalance = account
    ? convertedToPlanCurrencyAccountBalance - annualPrice
    : 0;
  const remainingFormattedBalance = formatBalanceWithCurrency({
    balance: remainingBalance,
    currency: plan?.currency,
  });

  const handleSubmit = async () => {
    if (!account) {
      toast('error', {
        title: 'Select an account',
      });

      return;
    }

    if (!planType) {
      return;
    }

    const newPlan = await subscribeToPlan({
      tariffId: plan.id,
    });

    setShouldShowFeeUpdateModal(true);

    navigate(`/plan-activation/${newPlan.id}/success`, {
      replace: true,
    });
  };

  if (!planType) {
    navigate(routesMap.home, {
      replace: true,
    });
  }

  console.log(account, rate);

  return (
    <div className="flex min-h-screen w-full flex-col bg-black px-[20px] md:px-[32px]">
      {isAccountsLoading || isPlansLoading ? (
        <PageLoader />
      ) : (
        <>
          <HeaderWrapper>
            <Link to={routesMap.home} data-test="logotype-button">
              <img
                src="/gcard-logotype.svg"
                alt="GCARD logotype"
                width={50}
                height={40}
                className="mr-6"
              />
            </Link>
          </HeaderWrapper>

          <div className="mx-auto flex w-full max-w-[400px] flex-grow flex-col pt-8">
            <h1
              className="leading-trim-both text-edge-cap mb-[44px]  text-[24px] font-medium leading-[120%] text-[#F6F6F9]"
              style={{
                fontFeatureSettings: "'liga' off, 'clig' off, 'calt' off",
              }}
            >
              {plan.name} plan activation
            </h1>

            <PlanCard
              tariff={plan}
              isActive={false}
              isDefault={false}
              isBestOffer={false}
              isPromoCard={false}
              className="mb-8 pt-0 sm:pt-0"
            />

            <div className="mb-6">
              <p
                className="mb-[6px] text-[16px] font-medium text-[#F6F6F9]"
                style={{
                  fontFeatureSettings:
                    "'cv02' on, 'cv03' on, 'cv04' on, 'cv11' on, 'liga' off, 'clig' off, 'calt' off",
                  fontVariantNumeric: 'lining-nums tabular-nums',
                  lineHeight: '150%',
                }}
              >
                From
              </p>
              <div className="flex flex-col rounded-[24px] border-[1.5px] border-[rgba(60,66,82,0.20)]">
                <div className=" flex items-center justify-between rounded-[10px_10px_0px_0px] bg-[#1A1D1F] p-[16px] ">
                  <span className="text-[20px] font-medium text-white">
                    {formatBalance(annualPrice)}
                  </span>
                  <div className="flex items-center gap-2 ">
                    <AccountSelect
                      account={account}
                      // @ts-ignore
                      onAccountChange={setAccount}
                      title="Select account"
                    />
                  </div>
                </div>
                {account && Boolean(rate) && (
                  <Details
                    annualPrice={annualFormattedPrice}
                    currentBalance={formattedAccountBalance}
                    insufficientFunds={
                      remainingBalance < 0
                        ? remainingFormattedBalance
                        : undefined
                    }
                    remainingBalance={
                      remainingBalance >= 0
                        ? remainingFormattedBalance
                        : undefined
                    }
                    nextBillingDate="20 oct"
                  />
                )}
              </div>
            </div>

            <Button
              className="mb-8 mt-auto flex w-full rounded-xl bg-white py-4 text-[16px] font-medium text-black hover:bg-gray-100"
              size="lg"
              onClick={handleSubmit}
              disabled={isPending || remainingBalance < 0}
            >
              {isPending && <Loader className="mr-4 h-4 w-4" size={16} />}
              Pay {annualFormattedPriceWithoutSpacing} and subscribe
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default PlanActivationPage;
